/**
 * Vite entry point. Migrated from CRA's src/index.js.
 *
 * Boot order:
 *  1. Initialise Sentry (no-op without VITE_SENTRY_DSN)
 *  2. Mount the React tree behind a Sentry ErrorBoundary
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary, initSentry } from './lib/sentry';
import './index.css';

initSentry();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

function GlobalErrorFallback({ error }: { error: unknown }): React.ReactElement {
  const message = error instanceof Error ? error.message : 'Unknown error';
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="mb-2 text-xl font-semibold text-slate-900">Something went wrong</h1>
        <p className="mb-4 text-sm text-slate-600">
          The app hit an unexpected error. The team has been notified.
        </p>
        <details className="mb-4 text-xs text-slate-500">
          <summary className="cursor-pointer">Technical details</summary>
          <pre className="mt-2 max-h-40 overflow-auto rounded bg-slate-100 p-2">{message}</pre>
        </details>
        <button
          type="button"
          onClick={() => window.location.assign('/')}
          className="inline-flex h-9 items-center justify-center rounded-md bg-blue-600 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <ErrorBoundary
      fallback={(props) => <GlobalErrorFallback error={props.error} />}
      showDialog={false}
    >
      <BrowserRouter>
        <AuthProvider>
          <App />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
